import { c as _c } from "react/compiler-runtime";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { DEFAULT_TABLE_PAGE_SIZE, FaroTable } from "@components/common/faro-table/faro-table";
import { creditTransactionsSelector, fetchingTransactionsFlagsSelector, transactionPaginationSelector } from "@store/credit-transactions/credit-transactions-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { TransactionsHeaders } from "@components/table/transactions/transactions-table-utils";
import { getTransactionsColumns } from "@components/table/transactions/transactions-columns";
import { useDateTime } from "@hooks/use-date-time";
import { useMembers } from "@hooks/use-members";
import { Box } from "@mui-wrapper";
import { projectsMapByIdSelector } from "@store/projects/projects-selector";
import { useCallback, useMemo, useState } from "react";
import { getMembersMapById } from "@utils/member-utils";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import { TableType } from "@utils/track-event/track-event-list";
import { fetchCreditTransactions } from "@store/credit-transactions/credit-transactions-thunk";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useActivityApiClient } from "@api/activity-api/use-activity-api-client";
import { EmptyIcon } from "@flat-ui";

/** List of required columns in the transactions table */
const requiredColumns = [TransactionsHeaders.date, TransactionsHeaders.user, TransactionsHeaders.description, TransactionsHeaders.project, TransactionsHeaders.credits];

/** Renders the table for transactions */
export function TransactionsTable() {
  const $ = _c(31);
  const transactions = useAppSelector(creditTransactionsSelector);
  const pagination = useAppSelector(transactionPaginationSelector);
  const {
    status
  } = useAppSelector(fetchingTransactionsFlagsSelector);
  const {
    formatDate
  } = useDateTime();
  const projectsMap = useAppSelector(projectsMapByIdSelector);
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  let t0;
  if ($[0] !== companyId) {
    t0 = {
      companyId
    };
    $[0] = companyId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== companyId || $[3] !== t0) {
    t1 = {
      companyId,
      activity: t0
    };
    $[2] = companyId;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const activityApiClient = useActivityApiClient(t1);
  const {
    companyMembers
  } = useMembers();
  let t2;
  if ($[5] !== companyMembers) {
    t2 = getMembersMapById(companyMembers);
    $[5] = companyMembers;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const membersMap = t2;
  const [page, setPage] = useState(0);
  const [isFetchingMoreTransactions, setIsFetchingMoreTransactions] = useState(false);
  const start = page * DEFAULT_TABLE_PAGE_SIZE;
  let t3;
  if ($[7] !== start || $[8] !== transactions) {
    t3 = transactions.slice(start, start + DEFAULT_TABLE_PAGE_SIZE);
    $[7] = start;
    $[8] = transactions;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const paginatedTransactions = t3;
  let t4;
  if ($[10] !== formatDate || $[11] !== membersMap || $[12] !== projectsMap) {
    t4 = requiredColumns.map(requiredColumn => getTransactionsColumns({
      formatDate,
      membersMap,
      projectsMap
    })[requiredColumn]);
    $[10] = formatDate;
    $[11] = membersMap;
    $[12] = projectsMap;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const transactionColumns = t4;
  let t5;
  if ($[14] !== activityApiClient || $[15] !== dispatch || $[16] !== pagination.after) {
    t5 = async () => {
      if (pagination.after) {
        setIsFetchingMoreTransactions(true);
        await dispatch(fetchCreditTransactions({
          activityApiClient,
          after: pagination.after
        }));
        setIsFetchingMoreTransactions(false);
      }
    };
    $[14] = activityApiClient;
    $[15] = dispatch;
    $[16] = pagination.after;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const loadMoreTransactions = t5;
  let t6;
  if ($[18] !== loadMoreTransactions || $[19] !== page || $[20] !== pagination.after) {
    t6 = newPage => {
      if (newPage > page && pagination.after) {
        loadMoreTransactions();
      }
      setPage(newPage);
    };
    $[18] = loadMoreTransactions;
    $[19] = page;
    $[20] = pagination.after;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const handlePageChange = t6;
  if (transactions.length === 0 && status === "succeeded") {
    let t7;
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <EmptyPage title="No transactions to show" subtitle="It looks like there are no transactions during this period." icon={EmptyIcon} />;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    return t7;
  }
  let t7;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      marginTop: SPACE_ELEMENTS_OF_MODAL,
      overflow: "hidden"
    };
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const t8 = status === "pending" || isFetchingMoreTransactions;
  let t9;
  if ($[24] !== handlePageChange || $[25] !== page || $[26] !== paginatedTransactions || $[27] !== pagination.totalCount || $[28] !== t8 || $[29] !== transactionColumns) {
    t9 = <Box sx={t7}><FaroTable entities={paginatedTransactions} uniqueIdKey="id" isLoading={t8} shouldHideCheckbox={true} columns={transactionColumns} rowHeight={70} totalCount={pagination.totalCount} currentPage={page} onPageChange={handlePageChange} tableType={TableType.creditTransactions} /></Box>;
    $[24] = handlePageChange;
    $[25] = page;
    $[26] = paginatedTransactions;
    $[27] = pagination.totalCount;
    $[28] = t8;
    $[29] = transactionColumns;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}